import axios from "@/utils/axios";
import type { AxiosRequestConfig } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@elaniintc/goes-design-system";

/**
 * Opts out of the global 401/403 redirects for confirm-association-detail,
 * where a 403 means "you are not a member of the group" and must render a
 * message instead of navigating away.
 */
const skipRedirectConfig: AxiosRequestConfig & {
  metadata?: { skip401Redirect?: boolean; skip403Redirect?: boolean };
} = {
  metadata: { skip401Redirect: true, skip403Redirect: true },
};

/**
 * Hooks para el nodo "Confirmar asociación de usuarios" (JER-84).
 *
 * Endpoint: GET /admin/v1/confirm-associate-user/{node_execution_id}
 *
 * Efecto secundario: el primer GET transiciona el status del trámite
 * de "pending" a "in_process" (implicit claim).
 */

export interface ApplicantProfile {
  id: string;
  first_name: string;
  last_name: string | null;
  document_type: string;
  document_number: string;
  email: string;
  phone?: string;
  category?: string;
  registration_phase?: "PRE_REGISTERED" | "REGISTERED" | "COMPLETED";
}

export interface AssociationDocument {
  id: string;
  name: string;
  url: string;
  mime_type?: string;
  size?: number;
}

/**
 * Alcance de las solicitudes que puede visualizar el usuario asociado.
 */
export type RequestsVisibility = "OWN" | "ALL";

export interface ConfirmAssociationDetail {
  id: string;
  display_name: string;
  title?: string;
  description?: string;
  node_execution_status: string;
  formality_request: {
    id: string;
    code: string;
    status: string;
    status_label: string;
  };
  represented: ApplicantProfile;
  representative: ApplicantProfile | null;
  target_is_unregistered: boolean;
  target_data: ApplicantProfile | null;
  access_type: string;
  authorization_status: string;
  authorization_ended_at: string | null;
  requests_visibility?: RequestsVisibility | null;
  can_reassign?: boolean | null;
  documents: AssociationDocument[];
  submitted_at: string;
  resolved_by_system: boolean;
}

export interface ConfirmAssociationDetailResponse {
  data: ConfirmAssociationDetail;
}

export const useGetConfirmAssociationDetail = (
  nodeExecutionId: string,
  enabled = true,
) => {
  return useQuery<ConfirmAssociationDetailResponse>({
    queryKey: ["confirm-association-detail", nodeExecutionId],
    queryFn: async () => {
      const response = await axios.get<ConfirmAssociationDetailResponse>(
        `/admin/v1/confirm-associate-user/${nodeExecutionId}`,
        skipRedirectConfig,
      );
      return response.data;
    },
    enabled: !!nodeExecutionId && enabled,
    retry: false,
  });
};

// ---------------------------------------------------------------------------
// POST /admin/v1/confirm-associate-user/{node_execution_id}/decide
// ---------------------------------------------------------------------------

interface DecideAssociationPayload {
  action: "approve" | "reject";
  rejection_reason?: string;
}

export const useDecideAssociation = (nodeExecutionId: string) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (payload: DecideAssociationPayload) => {
      const response = await axios.post<ConfirmAssociationDetailResponse>(
        `/admin/v1/confirm-associate-user/${nodeExecutionId}/decide`,
        payload,
      );
      return response.data;
    },
    onSuccess(data, variables) {
      // Actualizar cache con la respuesta (evita refetch)
      queryClient.setQueryData(
        ["confirm-association-detail", nodeExecutionId],
        data,
      );

      const isApproved = variables.action === "approve";
      toast.showToast({
        title: isApproved ? "Asociación aprobada" : "Asociación denegada",
        description: isApproved
          ? "La asociación ha sido aprobada correctamente."
          : "La asociación ha sido denegada.",
        options: {
          type: isApproved ? "success" : "info",
          duration: 5000,
        },
      });
    },
    onError(error: unknown) {
      const axiosErr = error as {
        response?: { status?: number; data?: { message?: string } };
      };
      const status = axiosErr?.response?.status;

      if (status === 409) {
        toast.showToast({
          title: "Decisión ya tomada",
          description:
            "Esta asociación ya fue decidida por otro administrador.",
          options: { type: "error", duration: 5000 },
        });
        // Refrescar para mostrar el estado actual
        queryClient.invalidateQueries({
          queryKey: ["confirm-association-detail", nodeExecutionId],
        });
      } else {
        toast.showToast({
          title: "Error",
          description:
            axiosErr?.response?.data?.message ||
            "Ocurrió un error al procesar la decisión.",
          options: { type: "error", duration: 5000 },
        });
      }
    },
  });
};
